<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import CButton from '@/components/shared/CButton';
import { resolveThumbnailUrl } from '../../../core/presets';
import crosswordIcon from '@/angular-app/images/template/crossword.svg';
import type { CrosswordStyle } from '../../../types';

const props = defineProps<{
    item: CrosswordStyle;
    disabled?: boolean;
    selected?: boolean;
}>();

const emit = defineEmits<{
    (event: 'edit', value: CrosswordStyle): void;
    (event: 'remove', value: CrosswordStyle): void;
}>();

const thumbnailFailed = ref(false);
const icon = computed(() => (thumbnailFailed.value ? crosswordIcon : resolveThumbnailUrl(props.item) || crosswordIcon));

watch(
    () => props.item.thumbnail,
    () => {
        thumbnailFailed.value = false;
    }
);
</script>

<template>
    <div class="d-flex align-items-center justify-content-between w-100">
        <div class="d-flex align-items-center">
            <span v-tippy="{ content: 'Use this id as the option value to select this style' }" class="style-id">
                {{ item.id }}.
            </span>
            <img :src="icon" class="style-icon mr-2" alt="" @error="thumbnailFailed = true" />
            <span>{{ item.name }}</span>
        </div>
        <div class="actions d-flex" :class="{ visible: selected }">
            <c-button size="icon" :icon="['far', 'pen']" :disabled="disabled" @click.stop="emit('edit', item)" />
            <c-button
                size="icon"
                :icon="['far', 'trash-can']"
                :disabled="disabled"
                @click.stop="emit('remove', item)"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
// The row lives in DraggableList, so the hover is on its .item-row
.actions {
    visibility: hidden;

    &.visible,
    .item-row:hover & {
        visibility: visible;
    }
}

.style-id {
    color: #919191;
    margin-right: 0.25rem;
    font-variant-numeric: tabular-nums;
    cursor: help;
}

.style-icon {
    width: 1.25rem;
    height: 1.25rem;
    object-fit: cover;
    border-radius: 0.125rem;
}
</style>
